/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect} from 'react'
import {KTIcon} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth'
import {AssetListToolbar} from './components/header/AssetListToolbar'
import {Helmet} from 'react-helmet'
import {EditAssetModel} from './components/EditAssetModal'
import {format, parse} from 'date-fns'
import ConfirmationModal from 'app/components/ConfirmationModal'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'
import Searchbar from 'app/components/Searchbar'
import AssetDataService from './services/asset.services'
import EmptyState from 'app/components/EmptyState'
import OwnerDisplay from 'app/components/OwnerDisplay'
import StatusIcon from 'app/components/StatusIcon'
import useAuditContext from 'app/Hooks/useAuditContext'

const AssetManagement: React.FC = () => {
  const [assets, setAssets] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [assetID, setAssetID] = useState('')
  const [isModalOpen2, setIsModalOpen2] = useState(false)
  const [sortBy, setSortBy] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const {currentAuditContext} = useAuditContext()
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    AssetDataService.getAssets(currentUser, setAssets, setShowEmptyState)
  }, [currentUser])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(assets)
      setSearchLength(false)
    } else {
      const filtered = assets.filter((t) => {
        const taskName = typeof t.name === 'string' ? t.name.toLowerCase() : ''
        return taskName.includes(searchTerm)
      })
      setFilteredItems(filtered)
      setSearchLength(filtered.length === 0)
    }
  }, [assets, searchTerm])

  const handleSort = (column) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)

    const sortedItems = [...filteredItems].sort((a, b) => {
      if (column === 'name' || column === 'os_version' || column === 'owner') {
        return newSortDirection === 'asc'
          ? a[column].localeCompare(b[column])
          : b[column].localeCompare(a[column])
      } else if (column === 'last_checkedin') {
        const dateA = new Date(a[column]).getTime()
        const dateB = new Date(b[column]).getTime()
        return newSortDirection === 'asc' ? dateA - dateB : dateB - dateA
      } else {
        return 0
      }
    })

    setFilteredItems(sortedItems)
  }

  const handleOpenModal2 = (id) => {
    setIsModalOpen2(true)
    setAssetID(id)
  }

  const handleCloseModal2 = () => {
    setIsModalOpen2(false)
  }

  const deleteHandler = async (assetData) => {
    setSelectedAsset(assetData)
    setIsDeleteModalOpen(true)
  }
  
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true) 
      await AssetDataService.deleteAsset(selectedAsset.id, selectedAsset.name, currentUser)
      showSuccessToast({message: 'Asset has been deleted successfully!'})
    } catch (error) {
      showErrorToast({message: 'Failed to delete asset'})
    } finally {
      setIsDeleting(false)  
      setIsDeleteModalOpen(false) 
    }
  }

  const formatTimestamp = (dateInput) => {
    if (typeof dateInput === 'string') {
      try {
        const date = parse(dateInput, 'yyyy-MM-dd', new Date())
        return format(date, 'd MMMM, yyyy')
      } catch (error) {
        return 'Invalid Date'
      }
    } else if (dateInput && dateInput.seconds) {
      return format(new Date(dateInput.seconds * 1000), 'd MMMM, yyyy')
    } else if (dateInput instanceof Date) {
      return format(dateInput, 'd MMMM, yyyy')
    } else {
      return 'Invalid Date'
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Asset Management</title>
      </Helmet>
      <div className='card-header border-0 pt-6 h-100'>
        <div className='card-title'>
          <div className='flex-lg-fill'>
              <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
        <div className='card-toolbar'>
          <AssetListToolbar currentAuditContext={currentAuditContext} />
        </div>
      </div>
      <div className='card-body py-0 '>
        <div className='table-responsive' style={{minHeight: '60vh', overflowY: 'auto'}}>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold ' style={{color:"#5A5A5A"}}>
                <th className='min-w-150px' onClick={() => handleSort('name')}>
                  NAME{' '}
                  {sortBy === 'name' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('os_version')}>
                  OS VERSION{' '}
                  {sortBy === 'os_version' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('owner')}>
                  OWNER{' '}
                  {sortBy === 'owner' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('last_checkedin')}>
                  LAST CHECKED IN{' '}
                  {sortBy === 'last_checkedin' && (
                    <span
                      className={`ms-1 icon-xxs text-muted ${
                        sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                      }`}
                    />
                  )}
                </th>
                <th className='min-w-50px'>HD ENCRYPTION</th>
                <th className='min-w-50px'>ANTI-VIRUS</th>
                <th className='min-w-50px'>PASSWORD POLICY</th>
                <th className='min-w-50px'>SCREEN POLICY</th>
                <th className='min-w-50px'>FIREWALL</th>
                <th className='min-w-50px'>IN AUDIT SCOPE</th>
                <th className='min-w-100px'></th>
              </tr>
            </thead>
            <EditAssetModel id={assetID} isOpen={isModalOpen2} onClose={handleCloseModal2} />
            {filteredItems && filteredItems.length > 0 ? (
              filteredItems
                .filter((data) => data.is_repo !== true)
                .map((assetsData) => {
                  return (
                    <tbody>
                      <tr key={assetsData.id}>
                        <td>
                          <span className='text-dark fw-bold d-block fs-6'>
                            {assetsData.name}
                          </span>
                        </td>
                        <td>
                          <span className='text-dark fw-semibold d-block fs-6'>
                            {assetsData.os_version}
                          </span>
                          <span className='fw-semibold text-gray-700 d-block fs-7'>
                            {assetsData.model}
                          </span>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            {assetsData.owner ? (
                              <OwnerDisplay owner={assetsData.owner} uid={assetsData.uid} />
                            ) : (
                              <>
                                <span>--</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <span className='tme-2 fs-6 fw-semibold'>
                                {assetsData.last_checkedin ? formatTimestamp(assetsData.last_checkedin) : "--"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.hd_encryption} style={{marginLeft: 20}} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.anti_virus} style={{marginLeft: 10}} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.password_policy} style={{marginLeft: 20}} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.screen_policy} style={{marginLeft: 20}} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.firewall} style={{marginLeft: 20}} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs'>
                                <StatusIcon value={assetsData?.audit_scope} style={{marginLeft: 20}} />
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                            onClick={() => handleOpenModal2(assetsData.id)}
                            data-bs-target='#kt_modal_2'
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <KTIcon iconName='pencil' className='fs-3' />
                          </button>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm '
                            onClick={(e) => deleteHandler(assetsData)}
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <KTIcon iconName='trash' className='fs-3 text-danger' />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  )
                })
            ) : (
              <tbody>
                <tr style={{height: '100%'}}>
                  <td colSpan={10}>
                    <div className='d-flex justify-content-center align-items-center text-center' style={{height: '100%'}}>
                      {searchLength ? (
                        <EmptyState
                          title="No Assets Found"
                          description="We couldn't find any assets that match your search. Maybe try a different search!"
                        />
                      ) : (
                        <EmptyState
                          title="No Assets Added"
                          description="Add the assets that all employees within your organization are required to adhere to."
                          isLoading={!showEmptyState}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
      <ConfirmationModal
      isModalOpen={isDeleteModalOpen}
      hideModal={() => setIsDeleteModalOpen(false)}
      confirmBtnClickHandler={handleConfirmDelete}
      toDeleteItemTitle={selectedAsset?.name}
      isLoading={isDeleting}
      title="Delete Asset"
      message={`Are you sure you want to delete ${selectedAsset?.name}? This action cannot be undone.`}
      type="delete"
      confirmButtonText="Remove"
    />
    </>
  )
}

export {AssetManagement}
